import { forwardRef } from 'react';
import type { ToolbarProps } from './Toolbar.types';

/**
 * Une clases ignorando valores vacíos, sin dependencias externas.
 */
const cx = (...parts: Array<string | false | undefined>): string =>
  parts.filter(Boolean).join(' ');

/**
 * Toolbar - Contenedor reutilizable para barras de herramientas.
 *
 * Solo pone la estructura (alto, espaciado, borde, sticky y salto de línea
 * opcional). Cada página decide qué botones, inputs y filtros van dentro,
 * usando los componentes de `@/contasis`.
 *
 * Tamaños:
 * - Por debajo de 1536 px (2xl): alto 48 px.
 * - Desde 1536 px: alto 64 px (igual que antes).
 * - Padding lateral de 24 px en todas las pantallas, para que la barra quede
 *   alineada con el contenido de los módulos.
 *
 * Reglas de diseño:
 * - Nunca usa `overflow-hidden`, para no recortar calendarios, menús,
 *   anillos de foco ni contadores de los botones.
 * - Usa `z-30`, la misma capa que el wrapper de ListHeader, y crea su propio
 *   contexto de apilamiento para quedar por encima de la tabla.
 * - No le pongas `transform`, `filter` ni `backdrop-filter`: romperían los
 *   paneles con `position: fixed` que viven dentro.
 * - Para respetar el notch, la página debe declarar
 *   `<meta name="viewport" content="..., viewport-fit=cover">`.
 *
 * @example
 * <Toolbar
 *   leftContent={<Input placeholder="Buscar producto" />}
 *   rightContent={<Button variant="primary">Nuevo producto</Button>}
 * />
 *
 * @example
 * // Muchos controles: el contenido salta de línea
 * <Toolbar
 *   wrap
 *   aria-label="Herramientas de la lista"
 *   data-tour="lista-toolbar"
 *   leftContent={<>{filtros}</>}
 *   rightContent={<>{acciones}</>}
 * />
 */
export const Toolbar = forwardRef<HTMLDivElement, ToolbarProps>(
  (
    {
      leftContent,
      rightContent,
      className,
      sticky = true,
      wrap = false,
      leftClassName,
      rightClassName,
      ...rest
    },
    ref
  ) => {
    const hasLeft = Boolean(leftContent);
    const hasRight = Boolean(rightContent);

    const containerClasses = cx(
      // Estructura y tokens del design system
      'flex items-center justify-between bg-surface-0 border-b border-[color:var(--border-default)] shrink-0',
      // Capa propia: popovers y calendarios quedan sobre la tabla
      'relative z-30',
      // Sticky solo desde md; en celular se desplaza con la página
      sticky && 'md:sticky md:top-0',
      // Padding lateral de 24 px (como el contenido), con margen seguro para notch
      'pl-[max(1.5rem,env(safe-area-inset-left))] pr-[max(1.5rem,env(safe-area-inset-right))]',
      // Alto y espaciado según modo
      wrap
        ? 'flex-wrap min-h-12 py-2 gap-x-3 gap-y-2 2xl:min-h-16 2xl:py-3 2xl:gap-x-4 2xl:gap-y-3'
        : 'h-12 gap-3 2xl:h-16 2xl:gap-4',
      className
    );

    const leftClasses = cx(
      'flex items-center min-w-0 gap-2 2xl:gap-3',
      wrap ? 'flex-wrap flex-[1_1_300px]' : 'flex-1',
      leftClassName
    );

    const rightClasses = cx(
      'flex items-center shrink-0 ml-auto gap-1.5 2xl:gap-2',
      rightClassName
    );

    return (
      <div ref={ref} className={containerClasses} {...rest}>
        {hasLeft && <div className={leftClasses}>{leftContent}</div>}
        {hasRight && <div className={rightClasses}>{rightContent}</div>}
      </div>
    );
  }
);

Toolbar.displayName = 'Toolbar';
